import {
  Suspense,
  lazy,
  useEffect,
  type ComponentType,
  type ReactNode,
} from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useAuthStore } from './stores/authStore.js';
import { useBrandingStore, useFeatureEnabled, useFeatures } from './stores/brandingStore.js';
import { useThemeStore } from './stores/themeStore.js';
import { hasAdminAccess } from './components/layout/navItems.js';
import { AppShell } from './components/layout/AppShell.js';
import { ProtectedRoute } from './components/auth/ProtectedRoute.js';
import { FeatureRoute } from './components/auth/FeatureRoute.js';
import { ManageRoute } from './components/auth/ManageRoute.js';
import { AdminLockGate } from './components/lock/AdminLockGate.js';
import { RoleGate } from './components/common/RoleGate.js';
import { ErrorBoundary } from './components/common/ErrorBoundary.js';
import { SkeletonRows } from './components/common/Skeleton.js';
// Not lazy: it renders a <Navigate/> and nothing else, so a separate chunk
// would only buy a Suspense flash on the way to the real destination.
import { RedirectToWorkspace } from './routes/reports/workspace/RedirectToWorkspace.js';
import { RedirectToSmart } from './routes/reports/smart/RedirectToSmart.js';
// Also not lazy, and for the same reason: it renders a <Navigate/>. These
// seven paths are retired reports whose figures the builder now composes from
// the same endpoints the pages called — see `retiredReports.ts` for which
// became which, and for the list of reports that CANNOT become questions.
import { RedirectToBuilder } from './routes/reports/builder/RedirectToBuilder.js';
import { RETIRED_REPORTS } from './routes/reports/builder/retiredReports.js';

/**
 * The retirement for a path, by its old address.
 *
 * Throws rather than falling back, and the throw is the point: a route
 * pointing at a retirement that no longer exists is a redirect to nowhere,
 * and failing at module load is how that is found in a test run instead of
 * by the reader who bookmarked it.
 */
function retired(from: string): (typeof RETIRED_REPORTS)[number] {
  const found = RETIRED_REPORTS.find((r) => r.from === from);
  if (found === undefined) throw new Error(`no retirement for ${from}`);
  return found;
}

// Lazily load page components so each route ships in its own chunk. Initial
// bundle drops dramatically and heavy deps (leaflet, recharts, react-leaflet)
// only load on the routes that need them.
function lazyNamed<K extends string>(
  loader: () => Promise<Record<K, ComponentType<Record<string, never>>>>,
  name: K
) {
  return lazy(async () => {
    const mod = await loader();
    return { default: mod[name] };
  });
}

const LoginPage = lazyNamed(() => import('./routes/auth/LoginPage.js'), 'LoginPage');
const ForgotPasswordPage = lazyNamed(
  () => import('./routes/auth/ForgotPasswordPage.js'),
  'ForgotPasswordPage'
);
const ResetPasswordPage = lazyNamed(
  () => import('./routes/auth/ResetPasswordPage.js'),
  'ResetPasswordPage'
);
const ChangePasswordPage = lazyNamed(
  () => import('./routes/auth/ChangePasswordPage.js'),
  'ChangePasswordPage'
);
const PersonalDashboardPage = lazyNamed(
  () => import('./routes/dashboard/DashboardPage.js'),
  'PersonalDashboardPage'
);
const AdminDashboardPage = lazyNamed(
  () => import('./routes/dashboard/DashboardPage.js'),
  'AdminDashboardPage'
);
const CombinedDashboardPage = lazyNamed(
  () => import('./routes/dashboard/DashboardPage.js'),
  'CombinedDashboardPage'
);
const EmployeeListPage = lazyNamed(
  () => import('./routes/employees/EmployeeListPage.js'),
  'EmployeeListPage'
);
const EmployeeDetailPage = lazyNamed(
  () => import('./routes/employees/EmployeeDetailPage.js'),
  'EmployeeDetailPage'
);
const WorkerAttendanceHistoryPage = lazyNamed(
  () => import('./routes/employees/WorkerAttendanceHistoryPage.js'),
  'WorkerAttendanceHistoryPage'
);
const EmployeeFormPage = lazyNamed(
  () => import('./routes/employees/EmployeeFormPage.js'),
  'EmployeeFormPage'
);
const BulkImportPage = lazyNamed(
  () => import('./routes/employees/BulkImportPage.js'),
  'BulkImportPage'
);
const AnnouncementsPage = lazyNamed(
  () => import('./routes/announcements/AnnouncementsPage.js'),
  'AnnouncementsPage'
);
const LettersPage = lazyNamed(() => import('./routes/letters/LettersPage.js'), 'LettersPage');
const ErpApprovalsPage = lazyNamed(
  () => import('./routes/erp/ErpApprovalsPage.js'),
  'ErpApprovalsPage'
);
const DepartmentsPage = lazyNamed(
  () => import('./routes/org/DepartmentsPage.js'),
  'DepartmentsPage'
);
const DesignationsPage = lazyNamed(
  () => import('./routes/org/DesignationsPage.js'),
  'DesignationsPage'
);
const BranchesPage = lazyNamed(() => import('./routes/org/BranchesPage.js'), 'BranchesPage');
const LaborSuppliersPage = lazyNamed(
  () => import('./routes/org/LaborSuppliersPage.js'),
  'LaborSuppliersPage'
);
const ProfilePage = lazyNamed(() => import('./routes/profile/ProfilePage.js'), 'ProfilePage');
const WorksiteListPage = lazyNamed(
  () => import('./routes/worksites/WorksiteListPage.js'),
  'WorksiteListPage'
);
const WorksiteFormPage = lazyNamed(
  () => import('./routes/worksites/WorksiteFormPage.js'),
  'WorksiteFormPage'
);
const SiteAttendanceHistoryPage = lazyNamed(
  () => import('./routes/worksites/SiteAttendanceHistoryPage.js'),
  'SiteAttendanceHistoryPage'
);
const WorksiteDetailPage = lazyNamed(
  () => import('./routes/worksites/WorksiteDetailPage.js'),
  'WorksiteDetailPage'
);
const CampOccupancyPage = lazyNamed(
  () => import('./routes/worksites/CampOccupancyPage.js'),
  'CampOccupancyPage'
);
const OperationsPage = lazyNamed(
  () => import('./routes/operations/OperationsPage.js'),
  'OperationsPage'
);
const MyAttendancePage = lazyNamed(
  () => import('./routes/attendance/MyAttendancePage.js'),
  'MyAttendancePage'
);
const CorrectionsPage = lazyNamed(
  () => import('./routes/attendance/CorrectionsPage.js'),
  'CorrectionsPage'
);
const AttendanceCalendarPage = lazyNamed(
  () => import('./routes/attendance/AttendanceCalendarPage.js'),
  'AttendanceCalendarPage'
);
const AttendanceDayPage = lazyNamed(
  () => import('./routes/attendance/AttendanceDayPage.js'),
  'AttendanceDayPage'
);
const ShiftsPage = lazyNamed(() => import('./routes/shifts/ShiftsPage.js'), 'ShiftsPage');
const SchedulePage = lazyNamed(() => import('./routes/shifts/SchedulePage.js'), 'SchedulePage');
const EmployeeLifecyclePage = lazyNamed(
  () => import('./routes/lifecycle/EmployeeLifecyclePage.js'),
  'EmployeeLifecyclePage'
);
const MyLeavePage = lazyNamed(() => import('./routes/leave/MyLeavePage.js'), 'MyLeavePage');
const LeaveManagePage = lazyNamed(
  () => import('./routes/leave/LeaveManagePage.js'),
  'LeaveManagePage'
);
const LeaveTypesPage = lazyNamed(
  () => import('./routes/leave/LeaveTypesPage.js'),
  'LeaveTypesPage'
);
const HolidaysPage = lazyNamed(() => import('./routes/leave/HolidaysPage.js'), 'HolidaysPage');
const PayrollPrepPage = lazyNamed(
  () => import('./routes/payroll/PayrollPrepPage.js'),
  'PayrollPrepPage'
);
const TeamDirectoryPage = lazyNamed(
  () => import('./routes/team/TeamDirectoryPage.js'),
  'TeamDirectoryPage'
);
const TeamAttendancePage = lazyNamed(
  () => import('./routes/team/TeamAttendancePage.js'),
  'TeamAttendancePage'
);
const ReportsLayout = lazyNamed(
  () => import('./routes/reports/ReportsLayout.js'),
  'ReportsLayout'
);
const ReportsHomePage = lazyNamed(
  () => import('./routes/reports/ReportsHomePage.js'),
  'ReportsHomePage'
);
const ReportsWorkspace = lazyNamed(
  () => import('./routes/reports/workspace/ReportsWorkspace.js'),
  'ReportsWorkspace'
);
const SmartReportPage = lazyNamed(
  () => import('./routes/reports/smart/SmartReportPage.js'),
  'SmartReportPage'
);
const TimesheetReport = lazyNamed(
  () => import('./routes/reports/TimesheetReport.js'),
  'TimesheetReport'
);
const SiteCampManpowerReport = lazyNamed(
  () => import('./routes/reports/SiteCampManpowerReport.js'),
  'SiteCampManpowerReport'
);
const ExecDashboard = lazyNamed(
  () => import('./routes/dashboard/ExecDashboard.js'),
  'ExecDashboard'
);
const LeaveBalanceReport = lazyNamed(
  () => import('./routes/reports/LeaveBalanceReport.js'),
  'LeaveBalanceReport'
);
const LabourCostReport = lazyNamed(
  () => import('./routes/reports/LabourCostReport.js'),
  'LabourCostReport'
);
const GratuityCalculatorPage = lazyNamed(
  () => import('./routes/reports/GratuityCalculatorPage.js'),
  'GratuityCalculatorPage'
);
const WpsExportPage = lazyNamed(
  () => import('./routes/reports/WpsExportPage.js'),
  'WpsExportPage'
);
const AuditLogPage = lazyNamed(
  () => import('./routes/system/AuditLogPage.js'),
  'AuditLogPage'
);
const JobsPage = lazyNamed(() => import('./routes/system/JobsPage.js'), 'JobsPage');
const SettingsPage = lazyNamed(
  () => import('./routes/settings/SettingsPage.js'),
  'SettingsPage'
);
const DevicesPage = lazyNamed(() => import('./routes/settings/DevicesPage.js'), 'DevicesPage');
const NotificationsPage = lazyNamed(
  () => import('./routes/notifications/NotificationsPage.js'),
  'NotificationsPage'
);
const NotFoundPage = lazyNamed(() => import('./routes/NotFoundPage.js'), 'NotFoundPage');

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30_000,
    },
  },
});

function RouteFallback(): JSX.Element {
  return (
    <div className="p-6">
      <SkeletonRows rows={6} cols={4} />
    </div>
  );
}

/**
 * Route guard for the staff "Bulk import" (/employees/import). That importer is
 * the login-based onboarding flow (it needs emails), which is wrong for the
 * construction profile — login-less site workers come from the CLI worker /
 * site-master import instead. So when the tenant runs the construction
 * terminology profile we hide the nav item *and* redirect the URL home, so the
 * page can't be reached by typing it. There is no "import" feature flag to gate
 * on, so this guards on the profile directly. Fail-open: an absent/legacy
 * terminology (non-construction) renders the page as before.
 */
function NonConstructionRoute({ children }: { children: ReactNode }): JSX.Element {
  const isConstruction = useBrandingStore(
    (s) => s.branding?.terminology?.profile === 'construction'
  );
  if (isConstruction) return <Navigate to="/employees" replace />;
  return <>{children}</>;
}

/**
 * Home ("/"). Everyone — admins included — lands on their personal workspace
 * dashboard (their "my day": check-in, my shift, my pending leave). Company
 * management lives behind the separate Admin panel. On a management-only console
 * (self-service off) there's no personal/admin split, so fall back to the
 * combined dashboard that shows everything the role can see.
 */
function HomeRoute(): JSX.Element {
  const selfService = useFeatureEnabled('selfService');
  return selfService ? <PersonalDashboardPage /> : <CombinedDashboardPage />;
}

/**
 * Admin-panel landing ("/overview") — the workforce/management overview a user
 * sees after opening the Admin panel. Anyone without management access is
 * redirected back to their workspace, so it's never a dead end for a
 * rank-and-file employee who lands on the URL directly.
 */
function AdminOverviewRoute(): JSX.Element {
  const role = useAuthStore((s) => s.user?.role);
  const features = useFeatures();
  if (!hasAdminAccess(role, features)) return <Navigate to="/" replace />;
  return <AdminDashboardPage />;
}

export function App(): JSX.Element {
  const bootstrap = useAuthStore((s) => s.bootstrap);
  const bootstrapBranding = useBrandingStore((s) => s.bootstrap);
  const bootstrapTheme = useThemeStore((s) => s.bootstrap);

  useEffect(() => {
    // Bootstrap order is intentional: theme first so the `.dark` class is on
    // <html> before any route renders — avoids a flash of the wrong palette
    // on initial paint.
    bootstrapTheme();
    void bootstrap();
    void bootstrapBranding();
  }, [bootstrap, bootstrapBranding, bootstrapTheme]);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter
          // Honour the Vite `base` so the app also works when served from a
          // subfolder (e.g. /demo/). BASE_URL is '/' in dev, '/demo/' when
          // built with VITE_BASE_PATH=/demo/ — react-router wants no
          // trailing slash.
          basename={import.meta.env.BASE_URL.replace(/\/+$/, '') || '/'}
        >
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route
                element={
                  <ProtectedRoute>
                    <AppShell />
                  </ProtectedRoute>
                }
              >
                {/* /change-password is under ProtectedRoute so the guard
                    can let the holder through (and bounce everyone else
                    off /change-password until they need it). */}
                <Route path="change-password" element={<ChangePasswordPage />} />
                <Route index element={<HomeRoute />} />
                {/* THE NAV AND THE ROUTE AGREE.
                    Every management route below whose sidebar entry declares
                    `roles` in navItems.ts is wrapped in <ManageRoute> with the
                    SAME list — navItems.ts is the source of truth, this is the
                    enforcement. Before this, the sidebar hid the link and the
                    URL still rendered the page, which is the worst of both: we
                    told the user they had no business here and then showed them
                    the screen anyway.

                    The role lists are written out literally rather than pulled
                    from a shared constant, so a route's rule is readable where
                    the route is — and so routeGates.test.tsx can compare the two
                    files as text and fail when a role-gated nav item gains no
                    matching gate.

                    Client-side defence in depth only: the server scopes and
                    authorises its own responses. The gate exists so we never
                    render a management screen to somebody who cannot use it.

                    PERSONAL routes stay ungated on purpose — `/`,
                    /my-attendance, /corrections, /leave, /announcements,
                    /letters, /profile, /notifications, /change-password are
                    every authenticated user's own workspace. (Note /leave is
                    personal while /leave/manage, /leave/calendar and
                    /leave/types are management.) */}
                <Route
                  path="overview"
                  element={
                    <ManageRoute roles={['ADMIN', 'HR', 'MANAGER']}>
                      <AdminOverviewRoute />
                    </ManageRoute>
                  }
                />
                {/* Check-in is no longer its own screen — the panel that owns
                    the GPS fix, the punch context and the button is the top of
                    the personal dashboard. The path stays as a redirect so
                    bookmarks, the old mobile CTA and any deep link still land
                    somewhere that can actually check you in. */}
                <Route path="punch" element={<Navigate to="/" replace />} />
                <Route
                  path="my-attendance"
                  element={
                    <FeatureRoute feature="selfService">
                      <MyAttendancePage />
                    </FeatureRoute>
                  }
                />
                <Route
                  path="corrections"
                  element={
                    <FeatureRoute feature="corrections">
                      <CorrectionsPage />
                    </FeatureRoute>
                  }
                />
                {/* THE APPROVER'S DOOR TO THE SAME PAGE.
                    `/corrections` is the worker's own screen and is deliberately
                    ungated, which is what keeps `isAdminPath` from flipping a
                    worker into the Admin panel for visiting it. But that also
                    meant the approval queue — which CorrectionsPage opens on for
                    an approver — existed ONLY in the personal list, and admin
                    mode renders no personal items. The app's second approval
                    queue could not be reached from the Admin panel at all.
                    A second path, role-gated, gives the queue a home on the
                    management surface without making the worker's own page an
                    admin route. The component is the same; only the door is
                    different. */}
                <Route
                  path="corrections/approvals"
                  element={
                    <ManageRoute roles={['ADMIN', 'HR', 'MANAGER']} feature="corrections">
                      <CorrectionsPage />
                    </ManageRoute>
                  }
                />
                {/* The muster roll. No FeatureRoute: attendance is the core of
                    every deployment, lean or full, so there is no module whose
                    absence would make this page meaningless — the group and
                    department controls gate themselves instead. */}
                <Route
                  path="attendance-calendar"
                  element={
                    <ManageRoute roles={['ADMIN', 'HR', 'MANAGER']}>
                      <AttendanceCalendarPage />
                    </ManageRoute>
                  }
                />
                {/* THE OTHER HALF OF THE MUSTER ROLL, AS ITS OWN ADDRESS.
                    The month is an aggregate you scan; this is the list of
                    people for one date, and at this client's size that list can
                    reach a thousand rows. One page cannot paginate two
                    populations — the list needs pages and the month aggregates
                    must not re-fetch every time somebody turns one — so they are
                    siblings sharing a filter bar rather than one screen doing
                    both.

                    A route rather than a tab, because every drill-through into
                    it names a DATE: a column header, a month tile's worst day, a
                    link pasted into chat. `?date=…&group=…&departmentId=…` is
                    the whole of that state, so Back works and the address can be
                    sent to somebody else.

                    Same gate as its sibling, deliberately: it is one screen's
                    worth of the same data, and a day view open to a role that
                    cannot see the month would be the hole moved rather than
                    closed. */}
                <Route
                  path="attendance-calendar/day"
                  element={
                    <ManageRoute roles={['ADMIN', 'HR', 'MANAGER']}>
                      <AttendanceDayPage />
                    </ManageRoute>
                  }
                />
                {/* THE OPERATIONS CONTROL ROOM — one screen where there were
                    four. The old paths stay as redirects rather than 404s:
                    they are in bookmarks, in shared links and in the mobile
                    CTA, and every one of them now lands on the view of
                    /operations that answers the same question it used to. */}
                <Route
                  path="operations"
                  element={
                    <ManageRoute roles={['ADMIN', 'HR', 'MANAGER']}>
                      <OperationsPage />
                    </ManageRoute>
                  }
                />
                {/* The two /live paths are redirects, so they need no gate of
                    their own: they land on /operations, which carries it. Same
                    for the /worksites/* and /reports/* redirects below. */}
                <Route
                  path="live"
                  element={<Navigate to="/operations?view=people" replace />}
                />
                <Route
                  path="live/map"
                  element={<Navigate to="/operations?view=map&pins=people" replace />}
                />
                <Route
                  path="team"
                  element={
                    <ManageRoute roles={['ADMIN', 'HR', 'MANAGER']} feature="team">
                      <TeamDirectoryPage />
                    </ManageRoute>
                  }
                />
                <Route
                  path="team-attendance"
                  element={
                    <ManageRoute roles={['ADMIN', 'HR', 'MANAGER']} feature="team">
                      <TeamAttendancePage />
                    </ManageRoute>
                  }
                />
                <Route
                  path="profile"
                  element={
                    <FeatureRoute feature="selfService">
                      <ProfilePage />
                    </FeatureRoute>
                  }
                />
                <Route path="notifications" element={<NotificationsPage />} />
                {/* A DETAIL ROUTE INHERITS ITS LIST'S ROLES. The nav only names
                    /employees, but /employees/new and every /employees/:id*
                    screen is the same records surface reached one click deeper —
                    gating the list and leaving the record open would move the
                    hole rather than close it. Same rule for /worksites/* and
                    /settings/* below. Bulk import is the one that differs,
                    because the nav gives it its own (narrower) list. */}
                <Route
                  path="employees"
                  element={
                    <ManageRoute roles={['ADMIN', 'HR', 'MANAGER']}>
                      <EmployeeListPage />
                    </ManageRoute>
                  }
                />
                <Route
                  path="employees/new"
                  element={
                    <ManageRoute roles={['ADMIN', 'HR', 'MANAGER']}>
                      <EmployeeFormPage />
                    </ManageRoute>
                  }
                />
                <Route
                  path="employees/import"
                  element={
                    <ManageRoute roles={['ADMIN', 'HR']}>
                      <NonConstructionRoute>
                        <BulkImportPage />
                      </NonConstructionRoute>
                    </ManageRoute>
                  }
                />
                <Route
                  path="employees/:id"
                  element={
                    <ManageRoute roles={['ADMIN', 'HR', 'MANAGER']}>
                      <EmployeeDetailPage />
                    </ManageRoute>
                  }
                />
                <Route
                  path="employees/:id/edit"
                  element={
                    <ManageRoute roles={['ADMIN', 'HR', 'MANAGER']}>
                      <EmployeeFormPage />
                    </ManageRoute>
                  }
                />
                <Route
                  path="employees/:id/attendance-history"
                  element={
                    <ManageRoute roles={['ADMIN', 'HR', 'MANAGER']}>
                      <WorkerAttendanceHistoryPage />
                    </ManageRoute>
                  }
                />
                <Route
                  path="employees/:id/lifecycle"
                  element={
                    <ManageRoute roles={['ADMIN', 'HR', 'MANAGER']} feature="lifecycle">
                      <EmployeeLifecyclePage />
                    </ManageRoute>
                  }
                />
                <Route
                  path="announcements"
                  element={
                    <FeatureRoute feature="announcements">
                      <AnnouncementsPage />
                    </FeatureRoute>
                  }
                />
                <Route
                  path="letters"
                  element={
                    <FeatureRoute feature="letters">
                      <LettersPage />
                    </FeatureRoute>
                  }
                />
                {/* THE SAME DOOR CORRECTIONS ALREADY HAS, and for the
                    same reason. `/letters` is ungated so a worker can request a
                    letter, which keeps `isAdminPath` from flipping them into
                    the Admin panel for visiting it — but that left the letter
                    QUEUE reachable only through a tab on a personal-list page,
                    and admin mode renders no personal rows. An approver had no
                    way to reach the app's fourth approval queue from the
                    management surface at all.

                    Role-gated, so the rail can carry it under Approvals beside
                    Correction requests and Leave approvals. Same component,
                    different door: the path is what opens it on the queue. */}
                <Route
                  path="letters/approvals"
                  element={
                    <ManageRoute roles={['ADMIN', 'HR', 'MANAGER']} feature="letters">
                      <LettersPage />
                    </ManageRoute>
                  }
                />
                {/* THE ERP EXCHANGE — one screen, two doors, for the reason
                    /letters/approvals has its own: the rail highlights by
                    comparing a row's `to` against the pathname with no query
                    awareness, so a `?tab=` view could never light its own row.

                    ADMIN/HR, matching the router. One half clears the file that
                    pays about a thousand people and the other rewrites payroll
                    routing; neither object belongs to a team, so unlike leave
                    and corrections there is nothing here for a MANAGER.

                    NOT behind `FeatureRoute`: the two ERP halves are switched
                    on individually in settings and both default to off, so the
                    screen has to be reachable in order to say so — a door that
                    vanishes when the integration is idle is a door nobody can
                    find to turn it on. */}
                <Route
                  path="erp/approvals"
                  element={
                    <ManageRoute roles={['ADMIN', 'HR']}>
                      <ErpApprovalsPage />
                    </ManageRoute>
                  }
                />
                <Route
                  path="erp/changes"
                  element={
                    <ManageRoute roles={['ADMIN', 'HR']}>
                      <ErpApprovalsPage />
                    </ManageRoute>
                  }
                />
                {/* The third view of the same screen: where the ERP is, and
                    whether the server holds a sign-in for it. Same roles as the
                    other two, because it is the same page. */}
                <Route
                  path="erp/connection"
                  element={
                    <ManageRoute roles={['ADMIN', 'HR']}>
                      <ErpApprovalsPage />
                    </ManageRoute>
                  }
                />
                <Route
                  path="departments"
                  element={
                    <ManageRoute roles={['ADMIN', 'HR']} feature="orgStructure">
                      <DepartmentsPage />
                    </ManageRoute>
                  }
                />
                <Route
                  path="designations"
                  element={
                    <ManageRoute roles={['ADMIN', 'HR']} feature="orgStructure">
                      <DesignationsPage />
                    </ManageRoute>
                  }
                />
                <Route
                  path="branches"
                  element={
                    <ManageRoute roles={['ADMIN', 'HR']}>
                      <BranchesPage />
                    </ManageRoute>
                  }
                />
                <Route
                  path="suppliers"
                  element={
                    <ManageRoute roles={['ADMIN', 'HR']}>
                      <LaborSuppliersPage />
                    </ManageRoute>
                  }
                />
                {/* Worksites / Sites & camps + the two live boards are the
                    site-workforce surface — gated behind the `workers` pillar so
                    an office-only tenant can't reach them (nav-hidden AND
                    URL-blocked). */}
                <Route
                  path="worksites"
                  element={
                    <ManageRoute roles={['ADMIN', 'HR', 'MANAGER']} feature="workers">
                      <WorksiteListPage />
                    </ManageRoute>
                  }
                />
                {/* Absorbed by /operations. The allocations map and the live
                    site board were both "the map view", so they land on it;
                    the site & camp board was the assigned-vs-actual table, so
                    it lands on that. No FeatureRoute here — the destination
                    decides what a tenant without the sites pillar can see, and
                    a redirect that 403s is worse than the page it replaced. */}
                <Route
                  path="worksites/allocations"
                  element={<Navigate to="/operations?view=map" replace />}
                />
                <Route
                  path="worksites/board"
                  element={<Navigate to="/operations?view=sites" replace />}
                />
                <Route
                  path="worksites/live-board"
                  element={<Navigate to="/operations?view=map" replace />}
                />
                <Route
                  path="worksites/new"
                  element={
                    <ManageRoute roles={['ADMIN', 'HR', 'MANAGER']} feature="workers">
                      <WorksiteFormPage />
                    </ManageRoute>
                  }
                />
                {/* THE PROJECT PAGE. Deliberately NARROWER than the /worksites
                    list it hangs off (ADMIN/HR, not ADMIN/HR/MANAGER), which is
                    the one place on this route table a detail screen does not
                    inherit its list's roles. A MANAGER is the HR line manager of
                    a set of PEOPLE; nothing in the model says who runs a SITE.
                    Letting one in would render a project page silently filtered
                    to their own direct reports — an answer to a different
                    question than the heading asks. Matches the API, which gates
                    /worksites/:id/time/* the same way. Revisit if
                    Worksite.managerId ever exists. */}
                <Route
                  path="worksites/:id"
                  element={
                    <ManageRoute roles={['ADMIN', 'HR']} feature="workers">
                      <WorksiteDetailPage />
                    </ManageRoute>
                  }
                />
                <Route
                  path="worksites/:id/edit"
                  element={
                    <ManageRoute roles={['ADMIN', 'HR', 'MANAGER']} feature="workers">
                      <WorksiteFormPage />
                    </ManageRoute>
                  }
                />
                <Route
                  path="worksites/:id/attendance-history"
                  element={
                    <ManageRoute roles={['ADMIN', 'HR', 'MANAGER']} feature="workers">
                      <SiteAttendanceHistoryPage />
                    </ManageRoute>
                  }
                />
                <Route
                  path="worksites/:id/camp-occupancy"
                  element={
                    <ManageRoute
                      roles={['ADMIN', 'HR', 'MANAGER']}
                      feature={['workers', 'camps']}
                    >
                      <CampOccupancyPage />
                    </ManageRoute>
                  }
                />
                <Route
                  path="shifts"
                  element={
                    <ManageRoute roles={['ADMIN', 'HR']} feature="shifts">
                      <ShiftsPage />
                    </ManageRoute>
                  }
                />
                <Route
                  path="schedule"
                  element={
                    <ManageRoute roles={['ADMIN', 'HR', 'MANAGER']} feature="shifts">
                      <SchedulePage />
                    </ManageRoute>
                  }
                />
                {/* `leave` is MY leave — personal, ungated. The management
                    leave screens under it are not, and the only thing telling
                    them apart is the path prefix. There are two of them now,
                    not three: `leave/manage` and `leave/calendar` are two
                    addresses of ONE page (the approvals queue and the month, as
                    two tabs), so they render the same element behind the same
                    gate. */}
                <Route
                  path="leave"
                  element={
                    <FeatureRoute feature="leave">
                      <MyLeavePage />
                    </FeatureRoute>
                  }
                />
                <Route
                  path="leave/manage"
                  element={
                    <ManageRoute roles={['ADMIN', 'HR', 'MANAGER']} feature="leave">
                      <LeaveManagePage />
                    </ManageRoute>
                  }
                />
                <Route
                  path="leave/types"
                  element={
                    <ManageRoute roles={['ADMIN', 'HR']} feature="leave">
                      <LeaveTypesPage />
                    </ManageRoute>
                  }
                />
                {/* THE SECOND ADDRESS OF THE SAME PAGE. The leave calendar was
                    its own screen with its own shell; it is a VIEW of the
                    approvals page now, and which of these two paths you are on
                    is what chooses the view. Deliberately NOT a redirect: this
                    path is in the rail and in people's bookmarks, and a rail
                    row whose `to` only redirects is both a row that never
                    highlights and a link the wiring test rejects. Same element
                    and the same gate as /leave/manage above — one page cannot
                    have two role lists, or a tab would be a hole. */}
                <Route
                  path="leave/calendar"
                  element={
                    <ManageRoute roles={['ADMIN', 'HR', 'MANAGER']} feature="leave">
                      <LeaveManagePage />
                    </ManageRoute>
                  }
                />
                <Route
                  path="holidays"
                  element={
                    <ManageRoute roles={['ADMIN', 'HR']} feature="leave">
                      <HolidaysPage />
                    </ManageRoute>
                  }
                />
                <Route
                  path="payroll"
                  element={
                    <ManageRoute roles={['ADMIN', 'HR']} feature="payroll">
                      <PayrollPrepPage />
                    </ManageRoute>
                  }
                />
                {/* The whole Reports section takes the nav entry's roles ONCE,
                    on the layout: every child renders inside this element's
                    <Outlet/>, so one gate here covers the catalog, the rail and
                    all twenty-odd reports — including any added later. The
                    layout deliberately takes no `feature`: its children are
                    gated on the module each one actually needs (`reports`,
                    `documents`, `leave`, `gratuity`, `payroll`), and hoisting
                    `reports` up here would take the document-expiry board away
                    from a tenant who runs documents without reports. */}
                <Route
                  path="reports"
                  element={
                    <ManageRoute roles={['ADMIN', 'HR', 'MANAGER']}>
                      <ReportsLayout />
                    </ManageRoute>
                  }
                >
                {/* THE FRONT DOOR IS THE SMART REPORT.

                    One bar: what you type becomes chips, the answer is live
                    underneath, and nothing on the page opens itself. It reads
                    the same endpoints every other report does, through the same
                    server-side `resolveReportScope`, so a MANAGER asking here
                    sees exactly the team they see everywhere else.

                    It is at the INDEX, and the navigator rail is gone, because
                    those two are the same decision. A section whose front door
                    is a search bar cannot also carry a menu of twenty-one names
                    beside it: the menu is the thing the bar replaces, and
                    keeping both would have made the reader read a list before
                    reaching the control that made the list unnecessary. The
                    reports that are screens rather than questions are offered
                    inside the bar — see `smart/destinations.ts`.

                    The workspace moved to `/reports/workspace`; the full
                    catalog stays at `/reports/all`. */}
                <Route
                  index
                  element={
                    <FeatureRoute feature="reports">
                      <SmartReportPage />
                    </FeatureRoute>
                  }
                />
                <Route
                  path="all"
                  element={
                    <FeatureRoute feature="reports">
                      <ReportsHomePage />
                    </FeatureRoute>
                  }
                />
                {/* The dynamic report builder — one composed sentence instead of
                    a hunt through the fixed catalog. It ADDS to the twenty-five
                    reports rather than replacing any of them: it reads the same
                    endpoints they do, and every one of those endpoints applies
                    `resolveReportScope` server-side, so a MANAGER composing a
                    question here sees exactly the team they see everywhere else.
                    `feature="reports"` and nothing more — the measures span
                    attendance, sites and suppliers, and each individual measure
                    is only as visible as the data behind it. */}
                {/* `/reports/smart` is where this screen lived for one
                    release. Kept so the links already sent land on it. */}
                <Route path="smart" element={<RedirectToSmart />} />
                {/* `/reports/builder` — the composer screen SmartReportPage
                    replaced. Unlike the fixed report pages below, it was never
                    linked from the catalog, the nav or any in-app control after
                    the smart report became the front door (audited and
                    confirmed: no `to: '/reports/builder'` anywhere in
                    catalog.ts or navItems.ts, and `OpenInBuilderLink` — the one
                    component that would have linked to it — is itself unused).
                    A route nobody links to is not "hidden", it is reachable
                    only by a bookmark or a hand-typed URL, and it deserves the
                    same courtesy every other retired path below gets: land on
                    the screen that replaced it rather than a dead end. */}
                <Route path="builder" element={<RedirectToSmart />} />
                {/* Retired paths. The routes stay so existing bookmarks, emailed
                    links and saved schedules keep resolving — they just land on
                    the view that replaced them, carrying their scope across.
                    `/reports/workspace` is where the workspace lived while the
                    job board held the front door. */}
                {/* The MyDeyar model: one filter bar drives five KPIs, two
                    charts and one detail table, and everything below the bar is
                    computed from the rows on screen. Reached from the bar on the
                    front door — it is a REPORT now rather than the section. */}
                <Route
                  path="workspace"
                  element={
                    <FeatureRoute feature="reports">
                      <ReportsWorkspace />
                    </FeatureRoute>
                  }
                />
                <Route
                  path="attendance"
                  element={<RedirectToWorkspace />}
                />
                <Route
                  path="suppliers"
                  element={
                    <FeatureRoute feature="reports">
                      <RedirectToBuilder report={retired('/reports/suppliers')} />
                    </FeatureRoute>
                  }
                />
                <Route
                  path="legal-entities"
                  element={
                    <FeatureRoute feature="reports">
                      <RedirectToBuilder report={retired('/reports/legal-entities')} />
                    </FeatureRoute>
                  }
                />
                <Route
                  path="late"
                  element={
                    <FeatureRoute feature="reports">
                      <RedirectToBuilder report={retired('/reports/late')} />
                    </FeatureRoute>
                  }
                />
                <Route
                  path="overtime"
                  element={
                    <FeatureRoute feature="reports">
                      <RedirectToBuilder report={retired('/reports/overtime')} />
                    </FeatureRoute>
                  }
                />
                {/* Both expiry paths are composed questions now — the board WAS
                    a grouping (four columns, one per bucket, of the same count)
                    and the flat list is the same count cut by document type.
                    The routes stay so bookmarks and emailed links keep
                    resolving; they land on the question that replaced them. */}
                <Route
                  path="document-expiry"
                  element={
                    <FeatureRoute feature="documents">
                      <RedirectToBuilder report={retired('/reports/document-expiry')} />
                    </FeatureRoute>
                  }
                />
                <Route
                  path="missed-punches"
                  element={
                    <FeatureRoute feature="reports">
                      <RedirectToBuilder report={retired('/reports/missed-punches')} />
                    </FeatureRoute>
                  }
                />
                <Route
                  path="headcount"
                  element={
                    <FeatureRoute feature="reports">
                      <RedirectToBuilder report={retired('/reports/headcount')} />
                    </FeatureRoute>
                  }
                />
                {/* THE SITE REPORTS NEED THE `workers` PILLAR. All five are
                    per-site/per-worker reports the rail already lists as
                    `feature: 'workers'` (two of them 'workers' + 'camps'), but
                    the routes asked only for `camps` or for nothing — so an
                    office-only tenant, whose rail correctly showed none of them,
                    could still open every one by URL and read an empty page.
                    One array per route now says exactly what the rail says. */}
                <Route
                  path="site-camp-manpower"
                  element={
                    <FeatureRoute feature={['reports', 'workers', 'camps']}>
                      <SiteCampManpowerReport />
                    </FeatureRoute>
                  }
                />
                <Route
                  path="manpower-trend"
                  element={
                    <FeatureRoute feature={['reports', 'workers']}>
                      <RedirectToBuilder report={retired('/reports/manpower-trend')} />
                    </FeatureRoute>
                  }
                />
                <Route
                  path="camp-capacity"
                  element={
                    <FeatureRoute feature={['reports', 'workers', 'camps']}>
                      <RedirectToBuilder report={retired('/reports/camp-capacity')} />
                    </FeatureRoute>
                  }
                />
                <Route
                  path="worker-site"
                  element={
                    <FeatureRoute feature={['reports', 'workers']}>
                      <RedirectToBuilder report={retired('/reports/worker-site')} />
                    </FeatureRoute>
                  }
                />
                <Route
                  path="worksite-hours"
                  element={
                    <FeatureRoute feature="reports">
                      <RedirectToBuilder report={retired('/reports/worksite-hours')} />
                    </FeatureRoute>
                  }
                />
                {/* Individual reports the RAIL restricts further than the
                    section does (catalog.ts `roles`). The section gate above
                    already settled ADMIN/HR/MANAGER; these three are ADMIN/HR
                    like the executive overview, so they gate the same way it
                    does — and refuse back to /reports rather than to /, because
                    a manager refused one report is still a reports user. */}
                <Route
                  path="document-expiry-buckets"
                  element={
                    <FeatureRoute feature="documents">
                      <RedirectToBuilder report={retired('/reports/document-expiry-buckets')} />
                    </FeatureRoute>
                  }
                />
                <Route
                  path="exec-overview"
                  element={
                    <FeatureRoute feature="reports">
                      {/* Payload is ADMIN/HR-only server-side; gate the route to
                          match so a MANAGER can't deep-link into a 403. */}
                      <RoleGate
                        allow={['ADMIN', 'HR']}
                        fallback={<Navigate to="/reports" replace />}
                      >
                        <ExecDashboard />
                      </RoleGate>
                    </FeatureRoute>
                  }
                />
                <Route
                  path="coverage"
                  element={
                    <FeatureRoute feature={['reports', 'workers']}>
                      <RedirectToBuilder report={retired('/reports/coverage')} />
                    </FeatureRoute>
                  }
                />
                <Route
                  path="compliance"
                  element={
                    <FeatureRoute feature="reports">
                      <RedirectToBuilder report={retired('/reports/compliance')} />
                    </FeatureRoute>
                  }
                />
                <Route
                  path="timesheet"
                  element={
                    <FeatureRoute feature="reports">
                      <TimesheetReport />
                    </FeatureRoute>
                  }
                />
                <Route
                  path="leave-balance"
                  element={
                    <FeatureRoute feature="leave">
                      <LeaveBalanceReport />
                    </FeatureRoute>
                  }
                />
                {/* Own-payroll labour cost allocated to sites. `reports` and
                    nothing more, matching the endpoint: the route it reads is
                    mounted behind requireFeature('reports') and takes
                    ADMIN/HR/MANAGER, which is exactly the gate the section
                    above already applied. Gating it on `payroll` as well would
                    hide a working report from a tenant who keeps salaries on
                    file without running payroll here. */}
                <Route
                  path="labour-cost"
                  element={
                    <FeatureRoute feature="reports">
                      <LabourCostReport />
                    </FeatureRoute>
                  }
                />
                <Route
                  path="gratuity"
                  element={
                    <FeatureRoute feature="gratuity">
                      <RoleGate
                        allow={['ADMIN', 'HR']}
                        fallback={<Navigate to="/reports" replace />}
                      >
                        <GratuityCalculatorPage />
                      </RoleGate>
                    </FeatureRoute>
                  }
                />
                <Route
                  path="wps"
                  element={
                    <FeatureRoute feature="payroll">
                      <RoleGate
                        allow={['ADMIN', 'HR']}
                        fallback={<Navigate to="/reports" replace />}
                      >
                        <WpsExportPage />
                      </RoleGate>
                    </FeatureRoute>
                  }
                />
                </Route>
                {/* ADMIN only — the two most sensitive screens in the app were
                    the two with no gate at all. */}
                <Route
                  path="system/audit-log"
                  element={
                    <ManageRoute roles={['ADMIN']}>
                      <AuditLogPage />
                    </ManageRoute>
                  }
                />
                <Route
                  path="system/jobs"
                  element={
                    <ManageRoute roles={['ADMIN']}>
                      <JobsPage />
                    </ManageRoute>
                  }
                />
                {/* Admin-PIN gate intentionally removed from Settings, Company
                    info and Branding — only the Kiosk Devices screen keeps it,
                    since that's the device/kiosk surface that needs protecting
                    on a shared tablet. The whole-app open lock (AppShell) is
                    unchanged.

                    AdminLockGate is NOT the role check: it is the kiosk PIN, and
                    it is inert unless the tenant enabled the app lock AND set an
                    admin PIN (see AdminLockGate — `status.enabled &&
                    hasAdminPin`). On an ordinary office deployment it passes
                    everyone straight through, which is why every settings route
                    also carries the nav entry's ADMIN/HR list on the outside.
                    The sub-pages inherit /settings' roles — one settings surface,
                    one rule. */}
                <Route
                  path="settings"
                  element={
                    <ManageRoute roles={['ADMIN', 'HR']}>
                      <AdminLockGate>
                        <SettingsPage />
                      </AdminLockGate>
                    </ManageRoute>
                  }
                />
                {/* Each settings group is a real URL, so "the pay rules" is a
                    link rather than an instruction to scroll. Declared AFTER
                    the static `settings/devices` route is not required — React
                    Router ranks a static segment above a dynamic one
                    regardless of order — but keeping it last also reads that
                    way. */}
                <Route
                  path="settings/g/:group"
                  element={
                    <ManageRoute roles={['ADMIN', 'HR']}>
                      <AdminLockGate>
                        <SettingsPage />
                      </AdminLockGate>
                    </ManageRoute>
                  }
                />
                {/* Company info and Branding used to be two routed pages here.
                    They are two cards of the "Company" settings tab now (see
                    `CompanyIdentityBlock` in SettingsPage.tsx) — these paths
                    stay alive as redirects for the same reason
                    settings/device-approvals below does: bookmarks and inbound
                    links the app does not control. */}
                <Route
                  path="settings/company"
                  element={<Navigate to="/settings/g/company" replace />}
                />
                <Route
                  path="settings/branding"
                  element={<Navigate to="/settings/g/company" replace />}
                />
                {/* THE ONE DEVICE SCREEN. Terminals, people's devices and the
                    approval queue are three views of it, chosen by
                    `?section=`/`?status=`. ADMIN + HR may read it; the DECIDE
                    buttons inside are ADMIN-only (RoleGate), because approving a
                    device grants an authentication capability. The kiosk PIN
                    (AdminLockGate) stands in front of it because it is a device
                    surface on a possibly-shared tablet. */}
                <Route
                  path="settings/devices"
                  element={
                    <ManageRoute roles={['ADMIN', 'HR']}>
                      <AdminLockGate>
                        <DevicesPage />
                      </AdminLockGate>
                    </ManageRoute>
                  }
                />
                {/* The approval queue used to be its own page here. The owner
                    asked for one device screen instead of two, so it is a VIEW
                    of the register now — and this path stays alive as a
                    redirect because it is in inbound traffic the app does not
                    control: bookmarks, the rail's old target, and the link in
                    the lockout email a person follows when they cannot sign in.
                    It carries the query string that lands them on the pending
                    slice, not on the terminal table. */}
                <Route
                  path="settings/device-approvals"
                  element={<Navigate to="/settings/devices?section=people&status=PENDING" replace />}
                />
                {/* Catch-all stays inside the AppShell so a wrong URL keeps the
                    sidebar/nav and shows a friendly 404 instead of a bounce. */}
                <Route path="*" element={<NotFoundPage />} />
              </Route>
            </Routes>
          </Suspense>
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
